import { Box, Typography, CircularProgress, Paper, Alert } from "@mui/material";
import { useState, useEffect } from "react";
import { useLogin, useNotify } from "react-admin";
import { USE_AUTH } from "@/config/auth";
import { AlertType } from "@/types";
import { AUTH_CONSTANTS } from "@/constants";
import { useAppTheme } from "@/hooks/useAppTheme";

/**
 * Custom Login Page for Microsoft Entra ID (formerly Azure AD) authentication
 */
export const EntraIdLoginPage = () => {
  const login = useLogin();
  const { isDarkMode } = useAppTheme();
  const [loading, setLoading] = useState(false);
  const [accessError, setAccessError] = useState<string | null>(null);
  const notify = useNotify();

  const { ERRORS } = AUTH_CONSTANTS;

  // If auth is disabled, redirect to home
  useEffect(() => {
    if (!USE_AUTH) {
      window.location.href = "/";
    }
  }, [USE_AUTH]);

  // Check for access denied redirect from failed /auth/me
  useEffect(() => {
    if (!USE_AUTH) return;

    const params = new URLSearchParams(window.location.search);
    const error = params.get("error");
    const message = params.get("message");

    if (error === ERRORS.ACCESS_DENIED) {
      const errorMessage = message || "You don't have access to this application. Please contact your administrator.";
      setAccessError(errorMessage);

      // Clean up the URL
      window.history.replaceState({}, "", window.location.pathname + window.location.hash);
    }
  }, [USE_AUTH]);

  // Don't render login page if auth is disabled
  if (!USE_AUTH) {
    return null;
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setAccessError(null);

    login({})
      .then(() => setLoading(false))
      .catch((error) => {
        setLoading(false);
        const errorMsg = typeof error === "string" ? error : error?.message || "Authentication failed";

        notify(errorMsg, {
          type: AlertType.ERROR,
          messageArgs: {
            _: errorMsg,
          },
        });
      });
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "background.default",
        p: 3,
        position: "relative",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          p: { xs: 4, sm: 6 },
          borderRadius: 4,
          border: "1px solid",
          borderColor: "divider",
          maxWidth: 400,
          width: "100%",
        }}
      >
        {/* Logo */}
        <Box
          component="img"
          src={isDarkMode ? "/aegle-icon-only-white.svg" : "/aegle-icon-only-dkblue.svg"}
          alt="Aegle Logo"
          sx={{ width: 56, height: 56, mb: 3 }}
        />

        {/* App Name */}
        <Typography variant="h5" sx={{ fontWeight: 600, color: "text.primary", mb: 0.5 }}>
          Aegle Claims Platform
        </Typography>

        {/* Company */}
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 4 }}>
          by Aegle Capital
        </Typography>

        {/* Access Denied Error */}
        {accessError && (
          <Alert severity={AlertType.ERROR} sx={{ width: "100%", mb: 3 }} onClose={() => setAccessError(null)}>
            {accessError}
          </Alert>
        )}

        {/* Microsoft Sign In Button */}
        <Box
          component="button"
          type="submit"
          disabled={loading}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
            width: "100%",
            px: 3,
            py: 1.5,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            bgcolor: "background.paper",
            cursor: loading ? "wait" : "pointer",
            transition: "all 0.15s ease",
            "&:hover:not(:disabled)": {
              bgcolor: "action.hover",
              borderColor: "action.selected",
            },
            "&:disabled": {
              opacity: 0.7,
            },
          }}
        >
          {loading ? <CircularProgress size={18} color="inherit" /> : <MicrosoftLogo />}
          <Typography variant="body2" sx={{ fontWeight: 500, color: "text.primary" }}>
            {loading ? "Signing in..." : "Sign in with Microsoft"}
          </Typography>
        </Box>
      </Paper>

      {/* Footer */}
      <Typography
        sx={{
          position: "absolute",
          bottom: 24,
          color: "text.disabled",
          fontSize: "0.75rem",
        }}
      >
        © {new Date().getFullYear()} Aegle Capital, Inc.
      </Typography>
    </Box>
  );
};

/** Microsoft four-square logo */
const MicrosoftLogo = () => (
  <svg width="18" height="18" viewBox="0 0 21 21" fill="none">
    <rect x="1" y="1" width="9" height="9" fill="#f25022" />
    <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
    <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
    <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
  </svg>
);
